import { useTranslation } from 'react-i18next';

import { formatCalendarDate, formatClock } from '@/shared/lib/datetime';

import type { ActivityEvent } from '@/entities/history/types';

interface DeadlinePayload {
  date: string;
  time: string | null;
  start_date: string | null;
  start_time: string | null;
}

const isDeadline = (value: unknown): value is DeadlinePayload =>
  typeof value === 'object' && value !== null && 'date' in value;

function deadlineText(value: unknown, locale: string): string {
  if (!isDeadline(value)) return '';
  const end =
    formatCalendarDate(value.date, locale, true) +
    (value.time ? ` ${formatClock(value.time)}` : '');
  if (!value.start_date) return end;
  const start =
    formatCalendarDate(value.start_date, locale, true) +
    (value.start_time ? ` ${formatClock(value.start_time)}` : '');
  return `${start} → ${end}`;
}

/**
 * The localized sentence of an event (without the actor): the event code (and its `change`
 * detail) selects the i18n key, the structured payload fills it. The DB never stores display
 * text, so an unknown future code still renders something sensible.
 */
export function useDescribeEvent(): (event: ActivityEvent) => string {
  const { t, i18n } = useTranslation();
  return (event) => {
    const payload = event.payload;
    const text = (key: string) => {
      const value = payload[key];
      return typeof value === 'string' ? value : '';
    };
    const untitled = t('checklist.untitled');
    const count = typeof payload.card_count === 'number' ? payload.card_count : 0;
    const params: Record<string, string | number> = {
      column_name: text('column_name'),
      from_column_name: text('from_column_name'),
      to_column_name: text('to_column_name'),
      source_title: text('source_title'),
      source_name: text('source_name'),
      name: text('name'),
      filename: text('filename'),
      from_board_name: text('from_board_name'),
      to_board_name: text('to_board_name'),
      item_text: text('item_text'),
      checklist: text('checklist_title') || untitled,
      label_name: text('label_name') || t('labels.unnamed'),
      from_version_number:
        typeof payload.from_version_number === 'number' ? payload.from_version_number : 0,
      count,
      completed: typeof payload.completed === 'number' ? payload.completed : 0,
      total: typeof payload.total === 'number' ? payload.total : 0,
      before: text('before'),
      after: text('after'),
    };
    let suffix = typeof payload.change === 'string' ? `_${payload.change}` : '';
    if (event.event_type === 'card.deadline_changed') {
      params.before = deadlineText(payload.before, i18n.language);
      params.after = deadlineText(payload.after, i18n.language);
      suffix = payload.after == null ? '_removed' : payload.before == null ? '_set' : '_changed';
    }
    if (event.event_type === 'card.checklist_changed' && payload.change === 'checklist_renamed') {
      params.before = text('before') || untitled;
      params.after = text('after') || untitled;
    }
    if (event.event_type === 'column.renamed' || event.event_type === 'board.renamed') {
      suffix = '';
    }
    // A card moved along with its whole column reads differently from a single-card move.
    if (event.event_type === 'card.moved_to_board' && payload.via === 'column') {
      suffix = '_column';
    }
    // Where a new card came from: a recurrence schedule or a Mysliarium note.
    if (
      event.event_type === 'card.created' &&
      (payload.via === 'recurrence' || payload.via === 'note')
    ) {
      suffix = `_${payload.via}`;
    }
    const key = `history.events.${event.event_type.replace('.', '_')}${suffix}`;
    // The key is built from the event code, so it cannot be checked at compile time.
    const translate = t as unknown as (key: string, options?: Record<string, unknown>) => string;
    return i18n.exists(key, { count })
      ? translate(key, params)
      : t('history.events.unknown', { type: event.event_type });
  };
}
